import { motion } from "framer-motion"
import { FC, ReactNode } from "react"
import { createPortal } from "react-dom"
import { autoUpdate, useFloating } from "@floating-ui/react"
import { ToolTip, ToolTipProps } from "./toolTip"

export type BadgeProps = {
  className?: string
  badgeClassName?: string
  count?: number
  content?: ReactNode
  children?: ReactNode
  bounce?: boolean
  bounceRepeat?: number
  /** Exposes the rendered badge to the e2e suite. */
  badgeTestId?: string
  toolTip?: ToolTipProps
}

/** The bounce: 0.3s each way, reversing, as many times as the caller asks for. */
const BOUNCE_TRANSITION = (repeat?: number) => ({
  duration: 0.3,
  repeat: repeat || Infinity,
  repeatType: "reverse" as const,
  ease: "easeInOut" as const,
});

/**
 * A count or icon marker on its child's top-right corner.
 *
 * The marker is PORTALLED to `document.body` and positioned `fixed` from the
 * wrapper's rect, rather than absolutely positioned inside the wrapper. Reason:
 * it hangs over the corner and bounces upward, so any ancestor that clips its
 * overflow cuts it in half — the workspace's context bar must carry
 * `overflow-y-hidden` (an `overflow-x-auto` collapses the other axis), and it
 * clipped the queue badge. `toolTip.tsx` already answers this exact problem the
 * same way; `autoUpdate` keeps the marker attached through scrolling and
 * resizing instead of reading the rect once.
 *
 * Positioning note: floating-ui owns the OUTER node's `transform`, and
 * `top-end` puts the marker's right edge on the child's right edge with its
 * bottom edge on the child's top edge. The inner wrapper then moves the marker
 * by half of its OWN width and height, which puts its CENTRE exactly on the
 * child's top-right corner — the marker straddles the corner instead of sitting
 * entirely inside it.
 *
 * The marker is also `pointer-events-none`: it is decoration sitting over the
 * child's own interactive corner, so it must never eat the click meant for the
 * button underneath. The one exception is a badge that carries a tooltip, whose
 * hover needs the marker back (`pointer-events-auto` on the marker itself).
 */
export const Badge: FC<BadgeProps> = (props) => {
  const { refs, floatingStyles } = useFloating({
    strategy: "fixed",
    placement: "top-end",
    whileElementsMounted: autoUpdate,
  })

  const count = props.count !== undefined && props.count > 0 ? props.count : null
  const hasMarker = count !== null || Boolean(props.content)

  const markerClass = `rounded-full min-w-4 h-4 px-1 text-xs flex items-center justify-center ${props.toolTip ? "pointer-events-auto" : ""} ${props.badgeClassName || ""}`

  return (
    <div ref={refs.setReference} className={`relative inline-block ${props.className || ""}`}>
      {props.children}
      {typeof window !== "undefined" && createPortal(
        hasMarker && (
          <div ref={refs.setFloating} style={floatingStyles} className="z-[9999] pointer-events-none">
            {/* Half of the marker's own size, so its centre lands on the corner. */}
            <div className="translate-x-1/2 translate-y-1/2">
              {count !== null && (
                <motion.div
                  key={count}
                  initial={{ y: 0 }}
                  animate={{ y: props.bounce ? -8 : 0 }}
                  transition={BOUNCE_TRANSITION(props.bounceRepeat)}
                >
                  <div data-testid={props.badgeTestId} className={markerClass}>
                    {props.toolTip
                      ? <ToolTip {...props.toolTip} placement="top">{count}</ToolTip>
                      : count}
                  </div>
                </motion.div>
              )}
              {props.content && (
                <motion.div
                  initial={{ y: 0 }}
                  animate={{ y: props.bounce ? -8 : 0 }}
                  transition={BOUNCE_TRANSITION(props.bounceRepeat)}
                >
                  <div data-testid={props.badgeTestId} className={markerClass}>
                    {props.toolTip
                      ? <ToolTip {...props.toolTip} placement="top">{props.content}</ToolTip>
                      : props.content}
                  </div>
                </motion.div>
              )}
            </div>
          </div>
        ),
        document.body
      )}
    </div>
  )
}